import {
  CASH_EXPENSE_CATEGORIES,
  CASH_INCOME_CATEGORIES,
  CASH_MANUAL_LINK_TYPES,
  CTM_DELIVERY_METHODS,
  exchangeRateOf,
  PAYROLL_LINKED_CATEGORIES,
  type ReauthKind,
} from '@felix/domain';
import { Button, Modal, SegmentedControl, Select, Textarea, TextInput } from '@mantine/core';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import { DayInput } from '../../inputs/DayInput.js';
import { errorCode } from '../personnel/source.js';
import classes from './Cash.module.css';
import type { CashData, CashPayrollItem, CashReconciliation, CashTxn, CtmItem } from './types.js';
import { amountOf, amountText, cashCurrencyChoices, formatMoney } from './view.js';

/**
 * Kasa pencereleri (ADR 0093): teyit, karar, ödeme düzenlemesi, iptal, gönderim, işlem,
 * bozdurma, ters kayıt, sayım, inceleme ve Payroll'dan işleme. Hata metni `cash.errors.codes`.
 */

export function useCashError() {
  const { t } = useTranslation();
  return (code: string) =>
    t(`cash.errors.codes.${code}`, { defaultValue: '' }) || t('cash.errors.save', { code });
}

interface ShellProps {
  title: string;
  text?: string | undefined;
  confirmLabel: string;
  tone?: 'danger';
  /** Onay ve imza türü: parola alanı yalnız ayar açıkken (ADR 0075). */
  reauthKind?: ReauthKind;
  disabled?: boolean;
  onClose: () => void;
  onSubmit: (password: { password?: string }) => Promise<void>;
  children?: ReactNode;
}

/** Ortak pencere: içerik, hata, parola alanı ve iki düğme. */
export function DialogShell(props: ShellProps) {
  const { t } = useTranslation();
  const message = useCashError();
  const reauth = useReauth(props.reauthKind);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const blocked = props.disabled === true || (props.reauthKind !== undefined && reauth.missing);
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={props.title}
      centered
      size="lg"
      closeButtonProps={{ 'aria-label': t('cash.closeWindow') }}
    >
      <form
        className={classes.dialogForm}
        onSubmit={async (event) => {
          event.preventDefault();
          if (blocked || busy) return;
          setBusy(true);
          setError(null);
          try {
            await props.onSubmit(props.reauthKind ? reauth.body() : {});
          } catch (failure) {
            reauth.noteError(failure);
            setError(message(errorCode(failure)));
            setBusy(false);
          }
        }}
      >
        {props.text ? <p className={classes.note}>{props.text}</p> : null}
        {props.children}
        {props.reauthKind ? (
          <ReauthField reauth={reauth} label={t('cash.password')} mb="0" />
        ) : null}
        {error ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
        <div className={classes.dialogButtons}>
          <Button variant="default" onClick={props.onClose}>
            {t('cash.cancelAction')}
          </Button>
          <Button
            type="submit"
            loading={busy}
            disabled={blocked}
            {...(props.tone === 'danger' ? { color: 'red' } : {})}
          >
            {props.confirmLabel}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;

function useLanguage() {
  const { i18n } = useTranslation();
  return i18n.resolvedLanguage ?? 'en';
}

/** Kaptanın teslim teyidi (ADR 0093 §3). */
export function ReceiptDialog(props: {
  item: CtmItem;
  today: string;
  onClose: () => void;
  onConfirm: (input: Record<string, unknown>, password: { password?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { item } = props;
  const [amount, setAmount] = useState(amountText(item.expectedAmount, language));
  const [day, setDay] = useState<string | null>(props.today);
  const [time, setTime] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const value = amountOf(amount, language);
  const differs = value !== null && value !== item.expectedAmount;
  return (
    <DialogShell
      title={t('cash.receipt.title', { no: item.ctmNo })}
      text={t('cash.receipt.text', { currency: item.currency })}
      confirmLabel={t('cash.receipt.confirm')}
      reauthKind="ctm_receipt"
      disabled={
        value === null ||
        value <= 0 ||
        !day ||
        (time !== '' && !TIME.test(time)) ||
        (differs && remarks.trim() === '')
      }
      onClose={props.onClose}
      onSubmit={(password) =>
        props.onConfirm(
          {
            amount: value,
            day,
            ...(time ? { time } : {}),
            receiptNo: receiptNo || null,
            remarks: remarks || null,
          },
          password,
        )
      }
    >
      <p className={classes.rule}>
        {t('cash.receipt.expected', {
          amount: `${item.currency} ${formatMoney(item.expectedAmount, language)}`,
        })}
      </p>
      <div className={classes.formGrid}>
        <TextInput
          label={`${t('cash.receipt.amount')} (${item.currency})`}
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
          error={amount !== '' && value === null ? t('cash.errors.codes.VALIDATION_FAILED') : null}
        />
        <TextInput
          label={t('cash.receipt.receiptNo')}
          value={receiptNo}
          maxLength={100}
          onChange={(e) => setReceiptNo(e.currentTarget.value)}
        />
        <DayInput
          label={t('cash.receipt.day')}
          required
          value={day}
          maxDate={props.today}
          onChange={setDay}
        />
        <TextInput
          label={t('cash.receipt.time')}
          placeholder="HH:MM"
          value={time}
          maxLength={5}
          onChange={(e) => setTime(e.currentTarget.value)}
        />
        <Textarea
          className={classes.wide}
          label={t('cash.receipt.remarks')}
          description={t('cash.receipt.reasonHint')}
          required={differs}
          autosize
          minRows={2}
          maxLength={1000}
          value={remarks}
          onChange={(e) => setRemarks(e.currentTarget.value)}
        />
      </div>
    </DialogShell>
  );
}

/**
 * Ofisin kararı: onay ya da gerekçeli iade (ADR 0093 §3). Son adımın onayında ofis gönderilecek
 * tutarı girer (az, eşit ya da fazla); talepten farklıysa gerekçe zorunlu ve kayda geçer; ödeme
 * aynı adımda düzenlenir (ADR 0110 §2).
 */
export function DecisionDialog(props: {
  item: CtmItem;
  decision: 'approved' | 'returned';
  today: string;
  onClose: () => void;
  onConfirm: (
    reason: string,
    password: { password?: string },
    transfer?: Record<string, unknown>,
  ) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { item } = props;
  const [reason, setReason] = useState('');
  const returning = props.decision === 'returned';
  const sending = !returning && item.pending?.final === true;
  const [amount, setAmount] = useState(amountText(item.requestedAmount, language));
  const [method, setMethod] = useState(item.deliveryMethod);
  const [day, setDay] = useState<string | null>(props.today);
  const [reference, setReference] = useState('');
  const [variance, setVariance] = useState('');
  const value = amountOf(amount, language);
  const differs = value !== null && value !== item.requestedAmount;
  const money = (minor: number) => `${item.currency} ${formatMoney(minor, language)}`;
  const blocked = sending
    ? value === null || value <= 0 || !day || (differs && variance.trim().length < 5)
    : returning && reason.trim() === '';
  return (
    <DialogShell
      title={t(returning ? 'cash.decision.returnTitle' : 'cash.decision.approveTitle', {
        no: item.ctmNo,
      })}
      text={
        returning
          ? t('cash.decision.returnText')
          : t(sending ? 'cash.decision.approveSendText' : 'cash.decision.approveText', {
              amount: money(item.requestedAmount),
            })
      }
      confirmLabel={t(
        returning
          ? 'cash.ctm.actions.return'
          : sending
            ? 'cash.decision.approveSend'
            : 'cash.ctm.actions.approve',
      )}
      reauthKind="ctm_request"
      disabled={blocked}
      onClose={props.onClose}
      onSubmit={(password) =>
        props.onConfirm(
          reason.trim(),
          password,
          sending
            ? {
                amount: value,
                method,
                arrangedOn: day,
                reference: reference.trim() || null,
                varianceReason: differs ? variance.trim() : null,
              }
            : undefined,
        )
      }
    >
      {sending ? (
        <div className={classes.formGrid}>
          <TextInput
            label={`${t('cash.decision.sendAmount')} (${item.currency})`}
            required
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.currentTarget.value)}
          />
          <Select
            label={t('cash.transfer.method')}
            data={CTM_DELIVERY_METHODS.map((m) => ({
              value: m,
              label: t(`cash.ctm.methods.${m}`),
            }))}
            value={method}
            allowDeselect={false}
            onChange={(v) => v && setMethod(v)}
          />
          <DayInput label={t('cash.transfer.arrangedOn')} required value={day} onChange={setDay} />
          <TextInput
            label={t('cash.transfer.reference')}
            value={reference}
            maxLength={200}
            onChange={(e) => setReference(e.currentTarget.value)}
          />
          <dl className={`${classes.wide} ${classes.varianceLine}`} aria-live="polite">
            <dt>{t('cash.decision.requested')}</dt>
            <dd>{money(item.requestedAmount)}</dd>
            <dt>{t('cash.decision.sending')}</dt>
            <dd>{value === null ? '—' : money(value)}</dd>
            <dt>{t('cash.decision.difference')}</dt>
            <dd data-tone={differs ? 'warning' : undefined}>
              {value === null ? '—' : money(value - item.requestedAmount)}
            </dd>
          </dl>
          {differs ? (
            <Textarea
              className={classes.wide}
              label={t('cash.decision.varianceReason')}
              description={t(
                value !== null && value < item.requestedAmount
                  ? 'cash.decision.partialHint'
                  : 'cash.decision.overHint',
              )}
              required
              autosize
              minRows={2}
              maxLength={500}
              value={variance}
              onChange={(e) => setVariance(e.currentTarget.value)}
            />
          ) : null}
        </div>
      ) : null}
      <Textarea
        label={t(returning ? 'cash.decision.reason' : 'cash.decision.note')}
        required={returning}
        autosize
        minRows={2}
        maxLength={1000}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
    </DialogShell>
  );
}

/** Ofisin ödeme / acente teslim düzenlemesi (ADR 0093 §3). */
export function TransferDialog(props: {
  item: CtmItem;
  today: string;
  onClose: () => void;
  onConfirm: (input: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { item } = props;
  const [amount, setAmount] = useState(amountText(item.requestedAmount, language));
  const [method, setMethod] = useState(item.deliveryMethod);
  const [day, setDay] = useState<string | null>(props.today);
  const [reference, setReference] = useState('');
  const [remarks, setRemarks] = useState('');
  const [variance, setVariance] = useState('');
  const value = amountOf(amount, language);
  // Kısmi ya da fazla gönderim gerekçeyle (ADR 0110 §2)
  const differs = value !== null && value !== item.requestedAmount;
  return (
    <DialogShell
      title={t('cash.transfer.title', { no: item.ctmNo })}
      confirmLabel={t('cash.transfer.save')}
      disabled={value === null || value <= 0 || !day || (differs && variance.trim().length < 5)}
      onClose={props.onClose}
      onSubmit={() =>
        props.onConfirm({
          amount: value,
          method,
          arrangedOn: day,
          reference: reference || null,
          remarks: remarks || null,
          varianceReason: differs ? variance.trim() : null,
        })
      }
    >
      <div className={classes.formGrid}>
        <TextInput
          label={`${t('cash.transfer.amount')} (${item.currency})`}
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
        />
        <Select
          label={t('cash.transfer.method')}
          data={CTM_DELIVERY_METHODS.map((m) => ({ value: m, label: t(`cash.ctm.methods.${m}`) }))}
          value={method}
          allowDeselect={false}
          onChange={(v) => v && setMethod(v)}
        />
        <DayInput label={t('cash.transfer.arrangedOn')} required value={day} onChange={setDay} />
        <TextInput
          label={t('cash.transfer.reference')}
          value={reference}
          maxLength={200}
          onChange={(e) => setReference(e.currentTarget.value)}
        />
        {differs ? (
          <Textarea
            className={classes.wide}
            label={t('cash.decision.varianceReason')}
            required
            autosize
            minRows={2}
            maxLength={500}
            value={variance}
            onChange={(e) => setVariance(e.currentTarget.value)}
          />
        ) : null}
        <Textarea
          className={classes.wide}
          label={t('cash.transfer.remarks')}
          autosize
          minRows={2}
          maxLength={1000}
          value={remarks}
          onChange={(e) => setRemarks(e.currentTarget.value)}
        />
      </div>
    </DialogShell>
  );
}

/** Gerekçeli işlem: talebin iptali ya da işlemin ters kaydı. */
export function ReasonDialog(props: {
  title: string;
  text: string;
  label: string;
  confirmLabel: string;
  onClose: () => void;
  onConfirm: (reason: string) => Promise<void>;
}) {
  const [reason, setReason] = useState('');
  return (
    <DialogShell
      title={props.title}
      text={props.text}
      confirmLabel={props.confirmLabel}
      tone="danger"
      disabled={reason.trim().length < 5}
      onClose={props.onClose}
      onSubmit={() => props.onConfirm(reason.trim())}
    >
      <Textarea
        label={props.label}
        required
        autosize
        minRows={2}
        maxLength={500}
        value={reason}
        onChange={(e) => setReason(e.currentTarget.value)}
      />
    </DialogShell>
  );
}

/** Gönderim onayı (parola yalnız ayar açıkken). */
export function SubmitDialog(props: {
  item: CtmItem;
  onClose: () => void;
  onConfirm: (password: { password?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  return (
    <DialogShell
      title={`${t('cash.ctm.actions.submit')} – ${props.item.ctmNo}`}
      text={t('cash.ctm.criticalRule')}
      confirmLabel={t('cash.ctm.actions.submit')}
      reauthKind="ctm_request"
      onClose={props.onClose}
      onSubmit={props.onConfirm}
    />
  );
}

const currencyOptions = (data: CashData, direction: 'in' | 'out') =>
  cashCurrencyChoices(data, direction).map((c) => ({ value: c, label: c }));
/** Seçili kasa seçenekler arasında değilse ilk seçenek. */
const initialCurrency = (data: CashData, direction: 'in' | 'out', wanted: string | null) => {
  const choices = cashCurrencyChoices(data, direction);
  return wanted && choices.includes(wanted) ? wanted : (choices[0] ?? wanted ?? 'USD');
};

/** Elle işlem: açılış, giriş ya da gider (ADR 0093 §4). */
export function EntryDialog(props: {
  kind: 'opening' | 'receipt' | 'expense';
  data: CashData;
  onClose: () => void;
  onConfirm: (input: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { kind, data } = props;
  const direction = kind === 'expense' ? 'out' : 'in';
  const [currency, setCurrency] = useState(() =>
    initialCurrency(data, direction, data.currency ?? 'USD'),
  );
  const [amount, setAmount] = useState('');
  const [day, setDay] = useState<string | null>(data.today);
  const [time, setTime] = useState('');
  const [category, setCategory] = useState<string | null>(null);
  const [payee, setPayee] = useState('');
  const [port, setPort] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const [linkedType, setLinkedType] = useState<string | null>(null);
  const [linkedRef, setLinkedRef] = useState('');
  const value = amountOf(amount, language);
  const categories =
    kind === 'expense'
      ? CASH_EXPENSE_CATEGORIES.filter((c) => !PAYROLL_LINKED_CATEGORIES.includes(c))
      : kind === 'receipt'
        ? CASH_INCOME_CATEGORIES
        : [];
  return (
    <DialogShell
      title={t(`cash.entry.title.${kind}`)}
      text={
        kind === 'expense'
          ? t('cash.entry.payrollNote')
          : kind === 'opening'
            ? t('cash.entry.openingNote')
            : undefined
      }
      confirmLabel={t('cash.entry.save')}
      disabled={
        value === null ||
        value <= 0 ||
        !day ||
        (time !== '' && !TIME.test(time)) ||
        (kind !== 'opening' && !category)
      }
      onClose={props.onClose}
      onSubmit={() =>
        props.onConfirm({
          txnType: kind,
          day,
          ...(time ? { time } : {}),
          amount: value,
          currency,
          category: kind === 'opening' ? null : category,
          payee: payee || null,
          port: port || null,
          receiptNo: receiptNo || null,
          remarks: remarks || null,
          linkedType: linkedType || null,
          linkedRef: linkedType ? linkedRef || null : null,
        })
      }
    >
      <div className={classes.formGrid}>
        <Select
          label={t('cash.entry.currency')}
          data={currencyOptions(data, direction)}
          value={currency}
          searchable
          allowDeselect={false}
          onChange={(v) => v && setCurrency(v)}
        />
        <TextInput
          label={t('cash.entry.amount')}
          required
          inputMode="decimal"
          value={amount}
          onChange={(e) => setAmount(e.currentTarget.value)}
          error={amount !== '' && value === null ? t('cash.errors.codes.VALIDATION_FAILED') : null}
        />
        <DayInput
          label={t('cash.entry.day')}
          required
          value={day}
          maxDate={data.today}
          onChange={setDay}
        />
        <TextInput
          label={t('cash.entry.time')}
          placeholder="HH:MM"
          value={time}
          maxLength={5}
          onChange={(e) => setTime(e.currentTarget.value)}
        />
        {kind !== 'opening' ? (
          <Select
            className={classes.wide}
            label={t('cash.entry.category')}
            required
            data={categories.map((c) => ({ value: c, label: t(`cash.txn.categories.${c}`) }))}
            value={category}
            onChange={setCategory}
          />
        ) : null}
        {kind !== 'opening' ? (
          <>
            <TextInput
              label={t('cash.entry.payee')}
              value={payee}
              maxLength={200}
              onChange={(e) => setPayee(e.currentTarget.value)}
            />
            <TextInput
              label={t('cash.entry.port')}
              value={port}
              maxLength={200}
              onChange={(e) => setPort(e.currentTarget.value)}
            />
            <TextInput
              label={t('cash.entry.receiptNo')}
              value={receiptNo}
              maxLength={100}
              onChange={(e) => setReceiptNo(e.currentTarget.value)}
            />
            <Select
              label={t('cash.entry.linkedType')}
              data={[
                { value: '', label: t('cash.entry.none') },
                ...CASH_MANUAL_LINK_TYPES.map((l) => ({
                  value: l,
                  label: t(`cash.entry.linkTypes.${l}`),
                })),
              ]}
              value={linkedType ?? ''}
              allowDeselect={false}
              onChange={(v) => setLinkedType(v || null)}
            />
            {linkedType ? (
              <TextInput
                label={t('cash.entry.linkedRef')}
                value={linkedRef}
                maxLength={100}
                onChange={(e) => setLinkedRef(e.currentTarget.value)}
              />
            ) : null}
          </>
        ) : null}
        <Textarea
          className={classes.wide}
          label={t('cash.entry.remarks')}
          autosize
          minRows={2}
          maxLength={1000}
          value={remarks}
          onChange={(e) => setRemarks(e.currentTarget.value)}
        />
      </div>
    </DialogShell>
  );
}

/** Döviz bozdurma: bağlı çıkış ve giriş, uygulanan kur (ADR 0093 §2). */
export function ExchangeDialog(props: {
  data: CashData;
  onClose: () => void;
  onConfirm: (input: Record<string, unknown>) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { data } = props;
  const [from, setFrom] = useState(() => initialCurrency(data, 'out', data.currency ?? 'EUR'));
  const [to, setTo] = useState(() =>
    initialCurrency(
      data,
      'in',
      cashCurrencyChoices(data, 'in').find((c) => c !== data.currency) ?? 'USD',
    ),
  );
  const [fromAmount, setFromAmount] = useState('');
  const [toAmount, setToAmount] = useState('');
  const [day, setDay] = useState<string | null>(data.today);
  const [payee, setPayee] = useState('');
  const [receiptNo, setReceiptNo] = useState('');
  const [remarks, setRemarks] = useState('');
  const out = amountOf(fromAmount, language);
  const inn = amountOf(toAmount, language);
  const rate = out && inn ? exchangeRateOf(out, inn) : null;
  return (
    <DialogShell
      title={t('cash.exchange.title')}
      text={t('cash.exchange.text')}
      confirmLabel={t('cash.entry.save')}
      disabled={!out || !inn || from === to || !day}
      onClose={props.onClose}
      onSubmit={() =>
        props.onConfirm({
          day,
          fromCurrency: from,
          fromAmount: out,
          toCurrency: to,
          toAmount: inn,
          payee: payee || null,
          receiptNo: receiptNo || null,
          remarks: remarks || null,
        })
      }
    >
      <div className={classes.formGrid}>
        <Select
          label={t('cash.exchange.from')}
          data={currencyOptions(data, 'out')}
          value={from}
          searchable
          allowDeselect={false}
          onChange={(v) => v && setFrom(v)}
        />
        <TextInput
          label={t('cash.exchange.fromAmount')}
          required
          inputMode="decimal"
          value={fromAmount}
          onChange={(e) => setFromAmount(e.currentTarget.value)}
        />
        <Select
          label={t('cash.exchange.to')}
          data={currencyOptions(data, 'in')}
          value={to}
          searchable
          allowDeselect={false}
          onChange={(v) => v && setTo(v)}
          error={from === to ? t('cash.errors.codes.SAME_CURRENCY') : null}
        />
        <TextInput
          label={t('cash.exchange.toAmount')}
          required
          inputMode="decimal"
          value={toAmount}
          onChange={(e) => setToAmount(e.currentTarget.value)}
        />
        <DayInput
          label={t('cash.entry.day')}
          required
          value={day}
          maxDate={data.today}
          onChange={setDay}
        />
        <TextInput
          label={t('cash.exchange.payee')}
          value={payee}
          maxLength={200}
          onChange={(e) => setPayee(e.currentTarget.value)}
        />
        <TextInput
          label={t('cash.entry.receiptNo')}
          value={receiptNo}
          maxLength={100}
          onChange={(e) => setReceiptNo(e.currentTarget.value)}
        />
        <Textarea
          className={classes.wide}
          label={t('cash.entry.remarks')}
          autosize
          minRows={2}
          maxLength={1000}
          value={remarks}
          onChange={(e) => setRemarks(e.currentTarget.value)}
        />
      </div>
      {rate ? <p className={classes.rule}>{t('cash.exchange.rate', { from, to, rate })}</p> : null}
    </DialogShell>
  );
}

/** Sayım: ay sonu, devir ya da devralma (ADR 0093 §5). */
export function ReconcileDialog(props: {
  kind: 'month_end' | 'handover' | 'takeover';
  currency: string;
  month: string;
  monthLabel: string;
  calculated: number;
  data: CashData;
  handover?: CashReconciliation | undefined;
  onClose: () => void;
  onConfirm: (body: Record<string, unknown>, password: { password?: string }) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const { kind, currency } = props;
  const [counted, setCounted] = useState(amountText(props.calculated, language));
  const [reason, setReason] = useState('');
  const [remarks, setRemarks] = useState('');
  const [handoverTo, setHandoverTo] = useState<string | null>(null);
  const value = amountOf(counted, language);
  const differs = value !== null && value !== props.calculated;
  const countedOn =
    kind === 'month_end'
      ? props.data.today.slice(0, 7) === props.month
        ? props.data.today
        : lastDay(props.month)
      : props.data.today;
  return (
    <DialogShell
      title={t(`cash.reconcile.title.${kind}`, { month: props.monthLabel, currency })}
      text={kind === 'month_end' ? t('cash.reconcile.lockNote') : t('cash.reconcile.handoverNote')}
      confirmLabel={t('cash.reconcile.confirm')}
      reauthKind="cash_reconciliation"
      disabled={
        value === null ||
        (differs && reason.trim().length < 5) ||
        (kind === 'handover' && !handoverTo)
      }
      onClose={props.onClose}
      onSubmit={(password) =>
        props.onConfirm(
          {
            kind,
            currency,
            month: kind === 'month_end' ? props.month : countedOn.slice(0, 7),
            countedOn,
            counted: value,
            differenceReason: differs ? reason.trim() : null,
            remarks: remarks.trim() || null,
            ...(kind === 'handover' ? { handoverTo } : {}),
            ...(kind === 'takeover' && props.handover ? { handoverId: props.handover.id } : {}),
          },
          password,
        )
      }
    >
      <p className={classes.rule}>
        {t('cash.statement.closing')}: {currency} {formatMoney(props.calculated, language)}
      </p>
      <div className={classes.formGrid}>
        <TextInput
          label={`${t('cash.reconcile.counted')} (${currency})`}
          required
          inputMode="decimal"
          value={counted}
          onChange={(e) => setCounted(e.currentTarget.value)}
        />
        {kind === 'handover' ? (
          <Select
            label={t('cash.reconcile.handoverTo')}
            required
            data={(props.data.crew ?? [])
              .filter((p) => p.id !== props.data.me)
              .map((p) => ({ value: p.id, label: p.name }))}
            value={handoverTo}
            onChange={setHandoverTo}
          />
        ) : (
          <div />
        )}
        <Textarea
          className={classes.wide}
          label={t('cash.reconcile.reason')}
          description={t('cash.reconcile.reasonHint')}
          required={differs}
          autosize
          minRows={2}
          maxLength={1000}
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <Textarea
          className={classes.wide}
          label={t('cash.reconcile.remarks')}
          autosize
          minRows={1}
          maxLength={1000}
          value={remarks}
          onChange={(e) => setRemarks(e.currentTarget.value)}
        />
      </div>
    </DialogShell>
  );
}

function lastDay(month: string): string {
  const [year, mon] = month.split('-').map(Number);
  const day = new Date(Date.UTC(year ?? 1970, mon ?? 1, 0)).getUTCDate();
  return `${month}-${String(day).padStart(2, '0')}`;
}

/** Ofisin sayım incelemesi (ADR 0093 §5). */
export function ReviewDialog(props: {
  title: string;
  onClose: () => void;
  onConfirm: (decision: 'approved' | 'queried', remarks: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const [decision, setDecision] = useState<'approved' | 'queried'>('approved');
  const [remarks, setRemarks] = useState('');
  return (
    <DialogShell
      title={props.title}
      confirmLabel={t('cash.transfer.save')}
      disabled={decision === 'queried' && remarks.trim().length < 5}
      onClose={props.onClose}
      onSubmit={() => props.onConfirm(decision, remarks.trim() || null)}
    >
      <div>
        <span className={classes.fieldLabel}>{t('cash.reconcile.reviewDecision')}</span>
        <SegmentedControl
          value={decision}
          onChange={(v) => setDecision(v as 'approved' | 'queried')}
          data={[
            { value: 'approved', label: t('cash.reconcile.review.approved') },
            { value: 'queried', label: t('cash.reconcile.review.queried') },
          ]}
        />
      </div>
      <Textarea
        label={t('cash.reconcile.reviewRemarks')}
        required={decision === 'queried'}
        autosize
        minRows={2}
        maxLength={1000}
        value={remarks}
        onChange={(e) => setRemarks(e.currentTarget.value)}
      />
    </DialogShell>
  );
}

/** Payroll'dan kasaya işleme: her kalem bir kez (ADR 0093 §6). */
export function PayrollPostDialog(props: {
  items: readonly CashPayrollItem[];
  onClose: () => void;
  onPost: (item: CashPayrollItem) => Promise<void>;
}) {
  const { t } = useTranslation();
  const language = useLanguage();
  const message = useCashError();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      opened
      onClose={props.onClose}
      title={t('cash.payroll.title')}
      centered
      size="lg"
      closeButtonProps={{ 'aria-label': t('cash.closeWindow') }}
    >
      <div className={classes.dialogForm}>
        <p className={classes.note}>{t('cash.payroll.text')}</p>
        {props.items.length === 0 ? (
          <p className={classes.note}>{t('cash.payroll.empty')}</p>
        ) : (
          <ul className={classes.payrollList}>
            {props.items.map((item) => (
              <li key={`${item.kind}-${item.id}`} className={classes.payrollItem}>
                <span>
                  <strong>{item.name}</strong>
                  <span className={classes.sub}>
                    {t(`cash.payroll.kinds.${item.kind}`)} · {item.label} · {item.day}
                  </span>
                </span>
                <span className={classes.nowrap}>
                  {item.currency} {formatMoney(item.amount, language)}{' '}
                  <Button
                    size="xs"
                    loading={busy === item.id}
                    onClick={async () => {
                      setBusy(item.id);
                      setError(null);
                      try {
                        await props.onPost(item);
                      } catch (failure) {
                        setError(message(errorCode(failure)));
                      }
                      setBusy(null);
                    }}
                  >
                    {t('cash.payroll.post')}
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        )}
        {error ? (
          <p className={classes.notice} data-tone="danger" role="alert">
            {error}
          </p>
        ) : null}
      </div>
    </Modal>
  );
}

export type { CashTxn };
